import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { isPast } from 'date-fns';
import { CheckCircle2, Edit, Pencil, Trash2, Users } from 'lucide-react';
import type { MatterMember } from '@/types/base';
import type { DeadlineTagVocabularyItem } from '@/hooks/useTagVocabulary';
import type { OutlookCalendar } from '@/hooks/useOutlookCalendars';
import { getStatusTransitions, getTaskStateConfig, normalizeTaskState } from '@/components/tasks/constants';
import { OutlookCalendarsSection } from '@/components/tasks/components/OutlookCalendarsSection';
import { AddButton } from '@/components/ui/add-button';
import { Button } from '@/components/ui/button';
import { DeadlineDueCell } from '@/components/ui/columns-table';
import {
  DeadlineTagList,
  formatDeadlineType,
  getTaskSourceDocuments,
  PartyCell,
  SourceDocumentLink,
  type Task,
  type TaskActionHandlers,
} from '@/components/ui/columns-tasks';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { getDocumentIcon } from '@/lib/document-icons';
import { toLocalDate } from '@/utils/temporal';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

interface DeadlineDetailPanelProps {
  task: Task | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slug: string;
  members?: MatterMember[];
  availableParties?: string[];
  // Controlled tag vocabulary, supplied by the page (not fetched per panel).
  tagVocabulary?: DeadlineTagVocabularyItem[];
  handlers: TaskActionHandlers;
  // Generic field update (description/notes), same path the Edit dialog uses.
  onEditTask?: (taskId: string, updates: Partial<Task>) => void;
  // Outlook calendar sync — omitted / disabled hides the section.
  availableCalendars?: OutlookCalendar[];
  outlookCalendarAvailable?: boolean;
  onSyncToCalendar?: (taskGuid: string, calendarId: string) => Promise<boolean>;
  onUnsyncFromCalendar?: (taskGuid: string, calendarId: string, myCalendarId?: string) => Promise<boolean>;
  isOutlookSyncing?: boolean;
}

function Section({
  title,
  action,
  children,
  className,
}: {
  title: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('border-t border-gray-100 px-6 py-4', className)}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{title}</h4>
        {action}
      </div>
      {children}
    </section>
  );
}

// Site-styled detail side panel for a deadline. Mirrors the Register tab's
// drawer (due date, sources, assignee, tags, status/actions) but built from real
// Task data and the app's own components + neutral gray palette. Body is keyed by
// task id so inline-edit drafts reset when a different row is opened.
export const DeadlineDetailPanel: React.FC<DeadlineDetailPanelProps> = ({
  task,
  open,
  onOpenChange,
  slug,
  members,
  availableParties = [],
  tagVocabulary = [],
  handlers,
  onEditTask,
  availableCalendars = [],
  outlookCalendarAvailable = false,
  onSyncToCalendar,
  onUnsyncFromCalendar,
  isOutlookSyncing = false,
}) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-lg">
        {task && (
          <DeadlineDetailBody
            key={task.id}
            task={task}
            slug={slug}
            members={members}
            availableParties={availableParties}
            tagVocabulary={tagVocabulary}
            handlers={handlers}
            onEditTask={onEditTask}
            availableCalendars={availableCalendars}
            outlookCalendarAvailable={outlookCalendarAvailable}
            onSyncToCalendar={onSyncToCalendar}
            onUnsyncFromCalendar={onUnsyncFromCalendar}
            isOutlookSyncing={isOutlookSyncing}
            onClose={() => onOpenChange(false)}
          />
        )}
      </SheetContent>
    </Sheet>
  );
};

const DeadlineDetailBody: React.FC<{
  task: Task;
  slug: string;
  members?: MatterMember[];
  availableParties: string[];
  tagVocabulary: DeadlineTagVocabularyItem[];
  handlers: TaskActionHandlers;
  onEditTask?: (taskId: string, updates: Partial<Task>) => void;
  availableCalendars: OutlookCalendar[];
  outlookCalendarAvailable: boolean;
  onSyncToCalendar?: (taskGuid: string, calendarId: string) => Promise<boolean>;
  onUnsyncFromCalendar?: (taskGuid: string, calendarId: string, myCalendarId?: string) => Promise<boolean>;
  isOutlookSyncing?: boolean;
  onClose: () => void;
}> = ({
  task,
  slug,
  members,
  availableParties,
  tagVocabulary,
  handlers,
  onEditTask,
  availableCalendars,
  outlookCalendarAvailable,
  onSyncToCalendar,
  onUnsyncFromCalendar,
  isOutlookSyncing = false,
  onClose,
}) => {
  const normalized = normalizeTaskState(task.state);
  const stateConfig = getTaskStateConfig(task.state);
  const parsedDate = toLocalDate(task.date);
  const isOverdue =
    !Number.isNaN(parsedDate.getTime()) && isPast(parsedDate) && normalized !== 'Completed';

  const sourceDocs = getTaskSourceDocuments(task);
  const isExtracted = !task.created_by;

  // Opening a source document opens the shared document panel, so close this
  // panel first to avoid stacking two overlays.
  const sourceHandlers: TaskActionHandlers = {
    ...handlers,
    onDocumentClick: handlers.onDocumentClick
      ? (documentGuid, taskId) => {
          onClose();
          handlers.onDocumentClick!(documentGuid, taskId);
        }
      : undefined,
  };

  // Status transitions available from here (mirrors the inline status control).
  const statusActions = getStatusTransitions({
    state: normalized,
    isExtracted,
    onMoveToNeedsConfirmation: () => handlers.onMoveToNeedsConfirmation(task.id),
    onConfirm: () => handlers.onConfirm(task.id),
    onComplete: () => handlers.onComplete(task.id),
  });

  return (
    <>
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-6 py-4 pr-12">
        <SheetTitle className="sr-only">{task.description || 'Deadline details'}</SheetTitle>
        <SheetDescription className="sr-only">Deadline details</SheetDescription>
        <div>
          <InlineEditableText
            value={task.description}
            placeholder="Untitled deadline"
            ariaLabel="Deadline description"
            onSave={(next) => {
              const trimmed = next.trim();
              if (trimmed && trimmed !== task.description) onEditTask?.(task.id, { description: trimmed });
            }}
            editable={!!onEditTask}
            displayClassName="text-lg font-semibold leading-snug text-gray-900"
            inputClassName="text-lg font-semibold leading-snug"
          />
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-sm text-gray-500">
          <Users className="h-3.5 w-3.5 shrink-0" />
          {handlers.onUpdateParty ? (
            <div className="-ml-2 min-w-0">
              <PartyCell task={task} uniqueParties={availableParties} onUpdateParty={handlers.onUpdateParty} />
            </div>
          ) : (
            <span>{task.party || 'Unknown'}</span>
          )}
        </div>
      </div>

      {/* Due date */}
      <Section title="Due date" className="border-t-0">
        <DeadlineDueCell
          task={task}
          onUpdateDate={handlers.onUpdateDate}
          slug={slug}
          handlers={sourceHandlers}
          showPossibleDates
        />
      </Section>

      {/* Status */}
      <Section title="Status">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium',
              stateConfig.bg,
              stateConfig.text,
            )}
          >
            {stateConfig.label}
          </span>
          {isOverdue && (
            <span className="inline-flex items-center rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-700">
              Overdue
            </span>
          )}
          {statusActions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 gap-1 text-xs">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Change status
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {statusActions.map((s) => (
                  <DropdownMenuItem key={s.label} onClick={s.action}>
                    {s.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </Section>

      {outlookCalendarAvailable &&
        !task.is_deleted &&
        availableCalendars.length > 0 &&
        onSyncToCalendar &&
        onUnsyncFromCalendar && (
          <Section
            title={
              <span className="flex items-center gap-1.5">
                <img src="/microsoft-outlook.svg" alt="" className="h-3.5 w-3.5 object-contain" />
                Outlook calendar
              </span>
            }
          >
            <OutlookCalendarsSection
              task={task}
              availableCalendars={availableCalendars}
              onSyncToCalendar={onSyncToCalendar}
              onUnsyncFromCalendar={onUnsyncFromCalendar}
              disabled={isOutlookSyncing}
            />
          </Section>
        )}

      {/* Deadline types */}
      {Array.isArray(task.deadline_types) && task.deadline_types.length > 0 && (
        <Section title="Type">
          <div className="flex flex-wrap items-center gap-1">
            {task.is_procedural && (
              <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 opacity-80">
                Procedural
              </span>
            )}
            {task.deadline_types.map((deadlineType) => (
              <span
                key={deadlineType}
                className="inline-flex items-center rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-500 opacity-80"
              >
                {formatDeadlineType(deadlineType)}
              </span>
            ))}
          </div>
        </Section>
      )}

      {/* Assignee */}
      <Section title="Assignee">
        {handlers.onUpdateAssignee && members && members.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="rounded-sm px-2 py-1 text-left text-sm transition-colors hover:bg-gray-100">
                {task.assigned_to_name || <span className="text-gray-400">Unassigned</span>}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={() => handlers.onUpdateAssignee!(task.id, null)}>
                <span className="italic text-gray-400">Unassigned</span>
              </DropdownMenuItem>
              {members.map((member) => (
                <DropdownMenuItem
                  key={member.user_guid}
                  onClick={() => handlers.onUpdateAssignee!(task.id, member.user_guid)}
                >
                  {member.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div className="text-sm">
            {task.assigned_to_name || <span className="text-gray-400">Unassigned</span>}
          </div>
        )}
      </Section>

      {/* Sources */}
      {sourceDocs.length > 0 && (
        <Section title="Sources">
          <div className="space-y-0.5">
            {sourceDocs.map((source, i) => {
              const { fileType } = getDocumentIcon(source.document_name || '', false);
              return (
                <div key={source.document_guid ?? i} className="flex items-start gap-1.5">
                  <span className="mt-1.5 shrink-0 font-mono text-[9px] font-semibold uppercase tracking-wider text-gray-400">
                    {fileType}
                  </span>
                  <SourceDocumentLink
                    source={source}
                    task={task}
                    slug={slug}
                    handlers={sourceHandlers}
                    showLabel
                    hideIcon
                    labelClassName="flex min-w-0 flex-1 items-center rounded px-1 py-1 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  />
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Tags */}
      <Section title="Tags">
        <DeadlineTagList
          task={task}
          vocabulary={tagVocabulary}
          handlers={handlers}
          revealAddOnHover={false}
        />
      </Section>

      {/* Notes */}
      <Section title="Notes">
        <InlineEditableText
          value={task.notes ?? ''}
          placeholder="Add notes…"
          addLabel="Add notes"
          ariaLabel="Notes"
          multiline
          editable={!!onEditTask}
          onSave={(next) => {
            if (next !== (task.notes ?? '')) onEditTask?.(task.id, { notes: next });
          }}
          displayClassName="whitespace-pre-wrap text-sm leading-relaxed text-gray-600"
          inputClassName="text-sm leading-relaxed"
        />
      </Section>

      {/* Footer actions */}
      <div className="mt-auto flex items-center justify-between gap-2 border-t border-gray-200 bg-gray-50/60 px-6 py-3">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-red-600 hover:bg-red-50 hover:text-red-700"
          onClick={() => {
            handlers.onDelete(task.id);
            onClose();
          }}
        >
          <Trash2 className="h-4 w-4" />
          Delete
        </Button>
        <Button
          variant="ghost"
          size="sm"
          icon={Edit}
          text="Edit"
          className="gap-1.5 text-gray-500 hover:text-gray-700"
          onClick={() => {
            handlers.onEdit(task);
            onClose();
          }}
        />
      </div>
    </>
  );
};

// Click-to-edit text/textarea used for the panel's description and notes. Saves
// on blur / Enter (Cmd+Enter for multiline), reverts on Escape.
const InlineEditableText: React.FC<{
  value: string;
  placeholder: string;
  /** When set, an empty value offers this as an "add" button instead of muted
   *  placeholder text. Omit where the empty state is a label, not an action. */
  addLabel?: string;
  ariaLabel: string;
  onSave: (next: string) => void;
  editable: boolean;
  multiline?: boolean;
  displayClassName?: string;
  inputClassName?: string;
}> = ({ value, placeholder, addLabel, ariaLabel, onSave, editable, multiline, displayClassName, inputClassName }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      const el = inputRef.current;
      if (el) el.setSelectionRange(el.value.length, el.value.length);
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    onSave(draft);
  };

  const cancel = () => {
    setEditing(false);
    setDraft(value);
  };

  if (!editable) {
    return value ? (
      <div className={displayClassName}>{value}</div>
    ) : (
      <div className="text-sm text-gray-400">{placeholder}</div>
    );
  }

  if (!editing) {
    if (!value && addLabel) {
      return <AddButton label={addLabel} onClick={() => setEditing(true)} />;
    }
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        title="Click to edit"
        className={cn(
          'group/inline -mx-1 flex w-full items-start gap-1 rounded-sm px-1 py-0.5 text-left transition-colors hover:bg-gray-100',
          value ? displayClassName : 'text-sm text-gray-400',
        )}
      >
        <span className="min-w-0 flex-1 whitespace-pre-wrap wrap-break-word">{value || placeholder}</span>
        <Pencil className="mt-1 h-3 w-3 shrink-0 text-gray-400 opacity-0 transition-opacity group-hover/inline:opacity-100" />
      </button>
    );
  }

  const commonProps = {
    ref: inputRef as never,
    value: draft,
    'aria-label': ariaLabel,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDraft(e.target.value),
    onBlur: commit,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
      } else if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        commit();
      }
    },
    className: inputClassName,
  };

  return multiline ? (
    <Textarea {...commonProps} rows={3} placeholder={placeholder} />
  ) : (
    <Input {...commonProps} placeholder={placeholder} />
  );
};

export default DeadlineDetailPanel;
